<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute } from 'vue-router'
import { api } from '../services/api'
import { store } from '../store'
import ConfirmDialog from '../components/ConfirmDialog.vue'

const route = useRoute()
const quiz = ref(null)
const answers = ref({})
const result = ref(null)
const error = ref('')
const loading = ref(false)
const seconds = ref(0)
const confirmSubmit = ref(false)
const onlyWrong = ref(false)
let timer

// Bài luyện tập: đồng hồ đếm lên, không tự nộp. Bài kiểm tra: đếm ngược và tự nộp khi hết giờ.
const isPractice = computed(() => quiz.value?.quiz_type === 'practice')
const isStudent = computed(() => store.user.role === 'student')
const isAnswered = id => String(answers.value[id] ?? '').trim() !== ''
const answeredCount = computed(() => quiz.value ? quiz.value.questions.filter(question => isAnswered(question.id)).length : 0)
const unanswered = computed(() => quiz.value ? quiz.value.questions.length - answeredCount.value : 0)
const progress = computed(() => quiz.value ? Math.round(answeredCount.value / quiz.value.questions.length * 100) : 0)
const timeText = computed(() => `${String(Math.floor(seconds.value / 60)).padStart(2, '0')}:${String(seconds.value % 60).padStart(2, '0')}`)
const lowTime = computed(() => !isPractice.value && seconds.value <= 60)
const visibleSolutions = computed(() => quiz.value.questions.map((question, index) => ({ question, index })).filter(({ question }) => !onlyWrong.value || answerStatus(resultFor(question.id)) !== 'correct'))

function startTimer() {
  clearInterval(timer)
  seconds.value = isPractice.value ? 0 : quiz.value.duration_minutes * 60
  timer = setInterval(() => {
    if (result.value) return
    if (isPractice.value) seconds.value++
    else if (seconds.value > 0) {
      seconds.value--
      if (seconds.value === 0) submit()
    }
  }, 1000)
}

async function load() {
  quiz.value = null
  result.value = null
  answers.value = {}
  error.value = ''
  clearInterval(timer)
  try {
    const data = await api(`/quizzes/${route.params.id}`)
    quiz.value = data.quiz
    startTimer()
  } catch (err) { error.value = err.message }
}

function askSubmit() {
  if (unanswered.value) confirmSubmit.value = true
  else submit()
}

async function submit() {
  if (!quiz.value || loading.value) return
  loading.value = true
  error.value = ''
  try {
    const data = await api(`/quizzes/${quiz.value.id}/submit`, { method: 'POST', body: JSON.stringify({ answers: answers.value }) })
    result.value = data.attempt
    confirmSubmit.value = false
    clearInterval(timer)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  } catch (err) { error.value = err.message }
  finally { loading.value = false }
}

function retry() {
  result.value = null
  answers.value = {}
  onlyWrong.value = false
  startTimer()
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

function jumpTo(question) {
  document.getElementById(`question-${question.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' })
}

function resultFor(questionId) { return result.value?.results.find(item => item.question_id === questionId) }
function answerStatus(item) { return item.is_correct === true ? 'correct' : item.is_correct === false ? 'wrong' : 'review' }
function difficultyLabel(value) { return value === 'advanced' ? 'Nâng cao' : value === 'application' ? 'Vận dụng' : 'Cơ bản' }
function responseText(question) {
  const response = resultFor(question.id).response
  if (!response) return '(Không trả lời)'
  return question.type === 'multiple_choice' ? `${String.fromCharCode(65 + Number(response))}. ${question.options[Number(response)] ?? ''}` : response
}

onMounted(load)
watch(() => route.params.id, load)
onUnmounted(() => clearInterval(timer))
</script>

<template>
  <div class="quiz-shell">
    <div v-if="error" class="error-banner">{{ error }}</div>
    <div v-if="!quiz && !error" class="loading-block"><div><div class="loader"></div><p>Đang chuẩn bị đề…</p></div></div>
    <template v-else-if="quiz && !result">
      <div v-if="quiz.lesson" class="breadcrumbs"><RouterLink :to="`/bai-hoc/${quiz.lesson.id}`">← Quay lại bài: {{ quiz.lesson.title }}</RouterLink></div>
      <section class="card">
        <div class="quiz-top"><div><span class="chip orange">{{ isPractice ? 'Luyện tập' : 'Bài kiểm tra' }}</span><h1>{{ quiz.title }}</h1><p>{{ quiz.subject }} · {{ quiz.chapter }} · {{ quiz.questions.length }} câu hỏi</p><p v-if="isPractice">Không giới hạn thời gian — em cứ làm thong thả, nộp khi sẵn sàng.</p><p v-else>Thời gian {{ quiz.duration_minutes }} phút. Hết giờ, bài được nộp tự động.</p><p v-if="quiz.questions.some(question => question.type === 'essay')">Câu tự luận có lời giải để em đối chiếu cùng người lớn sau khi nộp.</p></div></div>
      </section>

      <div class="quiz-sticky card">
        <div class="quiz-sticky-top">
          <span class="quiz-count"><b>{{ answeredCount }}/{{ quiz.questions.length }}</b> câu đã làm</span>
          <span class="quiz-clock" :class="{ low: lowTime }">{{ isPractice ? '◷ Đã làm' : '◷ Còn lại' }} <b>{{ timeText }}</b></span>
        </div>
        <div class="progress-track"><span :style="{ width: `${progress}%` }"></span></div>
        <div class="quiz-palette" aria-label="Chuyển nhanh tới câu hỏi">
          <button v-for="(question, index) in quiz.questions" :key="question.id" type="button" :class="{ done: isAnswered(question.id) }" :aria-label="`Câu ${index + 1}${isAnswered(question.id) ? ', đã trả lời' : ', chưa trả lời'}`" @click="jumpTo(question)">{{ index + 1 }}</button>
        </div>
      </div>

      <template v-for="(question, index) in quiz.questions" :key="question.id">
        <div v-if="isPractice && (index === 0 || quiz.questions[index - 1].difficulty !== question.difficulty)" class="section-heading difficulty-heading"><div><p class="eyebrow">Mức độ {{ question.difficulty === 'basic' ? '1' : question.difficulty === 'application' ? '2' : '3' }}</p><h2>{{ difficultyLabel(question.difficulty) }}</h2></div></div>
        <section :id="`question-${question.id}`" class="card question-card">
          <div class="question-label"><span>Câu {{ index + 1 }} · {{ question.type === 'multiple_choice' ? 'Trắc nghiệm' : question.type === 'fill_blank' ? 'Điền đáp án' : 'Tự luận' }} · <b>{{ difficultyLabel(question.difficulty) }}</b></span><span>{{ question.points }} điểm</span></div>
          <h2>{{ question.prompt }}</h2>
          <div v-if="question.type === 'multiple_choice'" class="answers">
            <label v-for="(option, optionIndex) in question.options" :key="option" class="answer-option" :class="{ selected: answers[question.id] === String(optionIndex) }"><input v-model="answers[question.id]" type="radio" :value="String(optionIndex)"><span class="option-letter">{{ String.fromCharCode(65 + optionIndex) }}</span>{{ option }}</label>
          </div>
          <input v-else-if="question.type === 'fill_blank'" v-model="answers[question.id]" class="form-control" placeholder="Nhập đáp án của em…">
          <textarea v-else v-model="answers[question.id]" class="form-control" rows="5" placeholder="Viết câu trả lời của em. Hãy nêu ý rõ ràng và đưa dẫn chứng khi cần…"></textarea>
        </section>
      </template>

      <div class="quiz-actions">
        <small v-if="!isStudent">Bạn đang xem đề với quyền quản trị; chỉ học sinh mới nộp bài.</small>
        <small v-else>{{ unanswered ? `Còn ${unanswered} câu chưa làm. Em vẫn có thể nộp và xem lời giải.` : 'Em đã làm hết các câu. Kiểm tra lại rồi nộp bài nhé!' }}</small>
        <button v-if="isStudent" class="btn btn-primary" :disabled="loading || answeredCount === 0" @click="askSubmit">{{ loading ? 'Đang chấm bài…' : `Nộp bài (${answeredCount}/${quiz.questions.length}) →` }}</button>
      </div>
    </template>

    <template v-else-if="result">
      <section class="card result-hero">
        <div class="score-circle"><strong>{{ Math.round(result.percent) }}%</strong></div>
        <p class="eyebrow">Kết quả bài làm</p>
        <h1>{{ result.percent >= 80 ? 'Tuyệt vời, em làm rất tốt!' : result.percent >= 60 ? 'Khá tốt, cố thêm một chút nhé!' : 'Mình cùng xem lại phần chưa chắc nhé!' }}</h1>
        <p v-if="result.pending_score">Phần máy chấm: {{ result.score }}/{{ result.max_score }} điểm · Phần tự luận {{ result.pending_score }} điểm chờ bố mẹ hoặc thầy cô chấm theo hướng dẫn chấm bên dưới</p>
        <p v-else>{{ result.score }}/{{ result.max_score }} điểm · {{ result.needs_teacher_review ? 'Câu tự luận được gợi ý theo tiêu chí; hãy xem lại cùng người lớn' : 'Đã chấm tự động' }}</p>
        <div class="result-actions">
          <button class="btn btn-light" @click="retry">↺ Làm lại</button>
          <RouterLink v-if="quiz.lesson" :to="`/bai-hoc/${quiz.lesson.id}`" class="btn btn-light">Xem lại bài học</RouterLink>
          <RouterLink v-if="quiz.next_lesson" :to="`/bai-hoc/${quiz.next_lesson.id}`" class="btn btn-primary">Bài tiếp theo: {{ quiz.next_lesson.title }} →</RouterLink>
          <RouterLink v-else :to="`/mon-hoc/${quiz.subject_slug}`" class="btn btn-primary">Về trang môn học →</RouterLink>
        </div>
      </section>
      <div class="section-heading"><div><h2>Đáp án và lời giải</h2><p>Hiểu cách làm quan trọng hơn ghi nhớ đáp án</p></div><label class="only-wrong"><input v-model="onlyWrong" type="checkbox"> Chỉ xem câu chưa đúng</label></div>
      <section class="solution-list">
        <p v-if="!visibleSolutions.length" class="card empty-state"><strong>Em làm đúng tất cả!</strong>Bỏ chọn “Chỉ xem câu chưa đúng” để xem lại toàn bộ lời giải.</p>
        <article v-for="{ question, index } in visibleSolutions" :key="question.id" class="card solution-card" :class="answerStatus(resultFor(question.id))">
          <div class="question-label"><span>Câu {{ index + 1 }}</span><span>{{ resultFor(question.id).points_awarded }}/{{ question.points }} điểm</span></div>
          <h3>{{ question.prompt }}</h3>
          <p><b>Bài làm:</b> {{ responseText(question) }}</p>
          <p v-if="resultFor(question.id).correct_answer !== null"><b>Đáp án:</b> {{ question.type === 'multiple_choice' ? `${String.fromCharCode(65 + Number(resultFor(question.id).correct_answer))}. ${question.options[Number(resultFor(question.id).correct_answer)]}` : resultFor(question.id).correct_answer }}</p>
          <p><b>Lời giải:</b> {{ resultFor(question.id).explanation }}</p>
          <p class="feedback">{{ resultFor(question.id).feedback }}</p>
        </article>
      </section>
    </template>
    <ConfirmDialog :open="confirmSubmit" icon="?" title="Nộp bài khi còn câu chưa làm?" confirm-label="Nộp luôn" cancel-label="Làm tiếp" busy-label="Đang chấm bài…" :busy="loading" @cancel="confirmSubmit = false" @confirm="submit">
      <p>Em còn <b>{{ unanswered }}</b> câu chưa trả lời. Câu bỏ trống sẽ không được tính điểm.</p>
    </ConfirmDialog>
  </div>
</template>
